import { Suspense } from "react";
import { useAtomValue } from "jotai";
import { SkeletonBlock } from "@zapier/design-system";
import {
  ContentfulTemplate,
  contentfulTemplatesAsyncAtom,
} from "@zapier/canvas-state";
import {
  ALLOWED_TEMPLATE_SLUGS,
  TEMPLATE_IMAGE_MAP,
} from "@zapier/canvas-constants";
import { TemplateThumbnail } from "../TemplateThumbnail";
import styles from "./TemplateThumbnails.module.css";

function TemplateThumbnailsLoader() {
  return Array.from({ length: ALLOWED_TEMPLATE_SLUGS.length }).map((_, i) => (
    <li key={i}>
      <SkeletonBlock width="240px" height="208px" />
    </li>
  ));
}

type Props = {
  selectedTemplateSlug?: string;
  handleTemplateClick: (template: ContentfulTemplate) => void;
  isDisabled?: boolean;
};

function SuspendedTemplateThumbnails({
  selectedTemplateSlug,
  handleTemplateClick,
  isDisabled,
}: Props) {
  const templates: ContentfulTemplate[] = useAtomValue(
    contentfulTemplatesAsyncAtom,
  );

  return templates.map((template) => {
    const slug = template.fields.slug;
    const isSelected = selectedTemplateSlug === slug;

    return (
      <li key={template.sys.id} className={styles.li}>
        <TemplateThumbnail
          name={template.fields.name}
          imageUrl={
            TEMPLATE_IMAGE_MAP[slug as keyof typeof TEMPLATE_IMAGE_MAP] ??
            template.fields.previewImage.fields.file.url
          }
          imageAltText={template.fields.previewImage.fields.title}
          onClick={() => handleTemplateClick(template)}
          isSelected={isSelected}
          isDisabled={isDisabled}
        />
      </li>
    );
  });
}

export function TemplateThumbnails({
  selectedTemplateSlug,
  handleTemplateClick,
  isDisabled,
}: Props) {
  return (
    <ul className={styles.ul}>
      <Suspense fallback={<TemplateThumbnailsLoader />}>
        <SuspendedTemplateThumbnails
          selectedTemplateSlug={selectedTemplateSlug}
          handleTemplateClick={handleTemplateClick}
          isDisabled={isDisabled}
        />
      </Suspense>
    </ul>
  );
}
